import React from "react";
import { Link, useLocation } from "react-router-dom";
import { SignedIn } from "@clerk/clerk-react";
import { SignInButton } from "../auth/SignInButton";

/**
 * Header Component
 *
 * This renders the top navigation bar for the MultiKeys portal.
 * It is styled to fit a dark-mode theme, utilizing the CSS custom variables defined in `index.css`.
 *
 * Dynamic Features:
 * - Highlights the active link based on the browser's current route location.
 * - Wraps private links (Dashboard, Configurator, Billing) in Clerk's `<SignedIn>` component so
 *   they are only rendered for logged-in users.
 * - Renders the unified `SignInButton` auth state controller on the right side.
 *
 * For non-coders:
 * - To change the navigation link names or destination paths, edit the array under the "Navigation Config" comment.
 * - To adjust header spacing, tweak the padding under the `headerStyle` object.
 */
export const Header: React.FC = () => {
  const location = useLocation();

  // --- STYLING CONSTANTS ---
  const headerStyle: React.CSSProperties = {
    backgroundColor: "var(--color-bg-secondary)",
    borderBottom: "1px solid var(--color-border)",
    padding: "0.85rem 2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 100,
  };

  const navListStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "1.75rem",
    listStyle: "none",
  };

  const getLinkStyle = (path: string): React.CSSProperties => {
    const isActive = location.pathname === path;
    return {
      color: isActive ? "var(--color-accent)" : "var(--color-text-secondary)",
      fontWeight: isActive ? 600 : 500,
      fontSize: "0.95rem",
      transition: "color var(--transition-speed) ease",
      position: "relative",
    };
  };

  return (
    <header style={headerStyle} data-testid="layout-header">
      {/* Brand logo/title linking back to home page */}
      <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
        <Link 
          to="/" 
          style={{ 
            color: "var(--color-text-primary)", 
            fontSize: "1.25rem", 
            fontWeight: 800, 
            letterSpacing: "0.5px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
          data-testid="header-logo"
        >
          {/* Neon accent cursor indicator icon */}
          <span style={{ color: "var(--color-accent)" }}>⌘</span>
          MultiKeys
        </Link>

        {/* Primary Navigation Links */}
        <nav aria-label="Main Navigation">
          <ul style={navListStyle}>
            <li>
              <Link to="/docs" style={getLinkStyle("/docs")} data-testid="nav-docs">
                Docs
              </Link>
            </li>
            <li>
              <Link to="/download" style={getLinkStyle("/download")} data-testid="nav-download">
                Download
              </Link>
            </li>

            {/* Authenticated user links - only visible when signed in */}
            <SignedIn>
              <li>
                <Link to="/dashboard" style={getLinkStyle("/dashboard")} data-testid="nav-dashboard">
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/configurator" style={getLinkStyle("/configurator")} data-testid="nav-configurator">
                  Configurator
                </Link>
              </li>
              <li>
                <Link to="/billing" style={getLinkStyle("/billing")} data-testid="nav-billing">
                  Billing
                </Link>
              </li>
            </SignedIn>
          </ul>
        </nav>
      </div>

      {/* Auth Control Button (Loading / SignedOut / SignedIn avatar) */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <SignInButton />
      </div>
    </header>
  );
};

export default Header;
